import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { generatePath } from 'react-router-dom'

import { isErrorType } from '@/config/http/http.helpers'
import history from '@/config/router/history'
import { PATHS } from '@/config/router/routes'
import { startAppListening } from '@/config/store/listenerMiddleware'
import { NOTIFICATION_APP_ID } from '@/constants/common'
import {
  selectFlags,
  selectHasUserInteracted,
  selectNotificationAppId
} from '@/ducks/app/selectors/app.selectors'
import {
  addOrderIdToPending,
  playAudioNotification,
  playAutoAcceptChimeNotification,
  removeOrderIdFromPending,
  stopAudioNotification
} from '@/ducks/app/slices/app.slice'
import { LocalizedString } from '@/ducks/common.schema'
import {
  WSNewOrderMessage,
  WSRejectedOrderMessage
} from '@/ducks/events/events.types'
import { eventActions } from '@/ducks/events/slice/events.slice'
import { addAutoAcceptOrderNotification, addNewOrderNotification } from '@/ducks/notifications/slices/notification.slice'
import { ORDER_STATUS_CODE } from '@/ducks/orders/order.constants'
import { OrderStatuses } from '@/ducks/orders/orders.types'
import { ordersApi } from '@/ducks/orders/services/orders.api'
import {
  createAutoAcceptBrowserNotification,
  createNewOrderBrowserNotification
} from '@/ducks/orders/utils/orders.utils'
import { ILoadingStates } from '@/ducks/slice.types'
import { selectStoreAutoAcceptEnabled, selectStoreId } from '@/ducks/store/selectors/store.selectors'

const recentAutoAcceptChimeOrderIds = new Set<string>()

export interface IPack {
  t_name: string
  t_description: string
  t_type: string
  sub_pack: {
    price: number
    _id: string
    name: string
  }[]
}

export interface FoodItem {
  barCode?: string
  isConfirmed?: boolean
  name: string
  id: string
  price: number
  slug: string
  quantity: number
  instruction: string
  barcode: string
  total: number
  mainCategory: string
  subCategory: string
  base_pack?: IPack[]
  addons?: IPack[]
  type_pack?: IPack[]
  size?: string
}

export interface IOrderDetails {
  user: {
    username: string
    lastName: string
    avatarUrl: string
  }
  isReady: boolean
  orderId: string
  foods: FoodItem[]
  status: {
    value: OrderStatuses
  }
  timeOut: number
  orderTime: number
  estimatedPreparationTime: number
  timeLeft: number
  isReturned: boolean
  isAssigned: boolean
  isReturnedToStock: boolean
  driver_name: string
  remainingETA: number
}

export interface OrderItem {
  driver_name: string
  status: OrderStatuses
  user: {
    firstName: string
    lastName: string
    avatarUrl: string
    userName: string
  }
  restaurantPayout?: number
  payout: string
  id: string
  foodCount: number
  timeOut: number
  orderTime: number
  estimatedPreparationTime: number
  timeLeft: number
  isReady: boolean
  isReturned: boolean
  isAssigned: boolean
  supportPhone: { code: string; number: string }
  child_details?: {
    avatar: string
    id: string
    name: LocalizedString
  }
  remainingETA: number
  autoAccepted?: boolean
  brand: string | null
}

export interface IBilling {
  subtotal: number
  discount: number
  serviceCharge: number
  taxAmount: number
  taxValue: number
  commission: number
  earnings: number
  paymentMethod: {
    paymentMethodDisplayName: string
    paymentMethodIcon: string
    paymentMethodDisplayName_i18n: {
      en: string
      fr: string
      ar: string
    }
  }
}

export type OrderFilters = Partial<{
  statuses: string[]
  start_date: string
  end_date: string
  start_price: number
  end_price: number
  brand: string
}>

export type SearchParams = Partial<{
  value: string
  category: 'ITEM' | 'ORDER_ID' | 'PERSON' | ''
}>

export interface OrdersState {
  driverNames: any
  loadingStates: {
    getOrdersRequest: ILoadingStates
  }

  ordersList?: OrderItem[] | null

  ordersDetailsMap: Record<string, IOrderDetails>
  billingsMap: Record<string, IBilling>

  filters: OrderFilters
  searchParams: SearchParams
}

const initialState: OrdersState = {
  loadingStates: {
    getOrdersRequest: 'initial'
  },

  ordersDetailsMap: {
    // 'YAF-1698124879182': orderDetailsMock,
    // 'YAF-1698124879183': orderDetailsMock2
  },
  billingsMap: {
    // 'YAF-1698124879182': mapBillingResponse(getBillingsResponseMock),
    // 'YAF-1698124879183': mapBillingResponse(getBillingsResponseMock)
  },
  filters: {},
  searchParams: {},
  driverNames: {}
}

export const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    resetOrdersStateToInitial: () => {
      return initialState
    },

    removeOrderFilter: (state, action: PayloadAction<string | string[]>) => {
      const names = Array.isArray(action.payload)
        ? action.payload
        : [action.payload]

      names.forEach(name => {
        if (state.filters.statuses?.includes(name)) {
          state.filters.statuses = state.filters.statuses.filter(
            status => status !== name
          )
        } else {
          const map = new Map(Object.entries(state.filters))
          map.delete(name)
          state.filters = Object.fromEntries(map.entries())
        }
      })
    },
    setOrderSearchParams: (state, action: PayloadAction<SearchParams>) => {
      state.searchParams = action.payload
    },
    setOrderFilters: (state, action: PayloadAction<OrderFilters>) => {
      state.filters = action.payload
    },
    setRemainingETAForOrder: (
      state,
      action: PayloadAction<{ orderId: string; remainingETA: number }>
    ) => {
      const { orderId, remainingETA } = action.payload
      if (state.ordersDetailsMap[orderId]) {
        state.ordersDetailsMap[orderId].remainingETA = remainingETA
      }
    },
    saveDriverName: (
      state,
      action: PayloadAction<{ orderId: string; driverName: string }>
    ) => {
      state.driverNames[action.payload.orderId] = action.payload.driverName
    }
  }
})

export const getOrderById = createAction<{ id: string }>('orders/getOrderById')
export const getBillingById = createAction<{ id: string }>(
  'orders/getBillingById'
)

startAppListening({
  actionCreator: eventActions.new_order,
  effect: async (action, listenerApi) => {
    const newOrderData = action.payload as WSNewOrderMessage
    const storeId = selectStoreId(listenerApi.getState()) as string
    const hasInteracted = selectHasUserInteracted(listenerApi.getState())
    const flags = selectFlags(listenerApi.getState())
    const isChainsEnabled =
      flags?.lmd_store_web_restaurant_chains_feature || false

    const isNewOrderFromChildRest =
      isChainsEnabled &&
      storeId &&
      newOrderData?.restaurant?.restaurant_parent_id === storeId

    if (
      newOrderData &&
      (newOrderData.restaurant._id === storeId || isNewOrderFromChildRest) &&
      newOrderData.status === ORDER_STATUS_CODE.ORDER_RECEIVED
    ) {
      const isAutoAcceptEnabled = selectStoreAutoAcceptEnabled(
        listenerApi.getState()
      )

      listenerApi.dispatch(
        ordersApi.endpoints.getOrdersCountByTypes.initiate({
          store_id: storeId
        })
      )
      listenerApi.dispatch(
        ordersApi.util.invalidateTags([{ type: 'Orders', id: 'LIST' }])
      )

      if (isAutoAcceptEnabled) {
        return
      }

      createNewOrderBrowserNotification(newOrderData.order_id)

      if (
        !history.location.pathname.includes(
          generatePath(PATHS.ORDERS_BY_STATUS, { status: 'new' })
        )
      ) {
        const detailPromise = listenerApi
          .dispatch(
            ordersApi.endpoints.fetchOrderDetails.initiate({
              storeId,
              orderId: newOrderData.order_id
            })
          )
          .unwrap()

        await detailPromise.then(details => {
          listenerApi.dispatch(
            addNewOrderNotification({
              orderDetails: {
                ...details,
                earnings: newOrderData.restaurant_payout
              },
              type: 'newOrder'
            })
          )
        })
      }

      listenerApi.dispatch(stopAudioNotification())
      let storageAppId = localStorage.getItem(NOTIFICATION_APP_ID)
      const currentAppId = selectNotificationAppId(listenerApi.getState())
      if (!storageAppId && currentAppId) {
        localStorage.setItem(NOTIFICATION_APP_ID, String(currentAppId))
        storageAppId = currentAppId
      }

      if (currentAppId === storageAppId) {
        listenerApi.dispatch(
          addOrderIdToPending({ orderId: newOrderData.order_id })
        )
        if (hasInteracted) {
          listenerApi.dispatch(playAudioNotification())
        }
      }
    }
  }
})

startAppListening({
  actionCreator: eventActions.new_order,
  effect: async (action, listenerApi) => {
    const newOrderData = action.payload as WSNewOrderMessage
    const storeId = selectStoreId(listenerApi.getState()) as string
    const flags = selectFlags(listenerApi.getState())
    const isChainsEnabled =
      flags?.lmd_store_web_restaurant_chains_feature || false

    const isNewOrderFromChildRest =
      isChainsEnabled &&
      storeId &&
      newOrderData?.restaurant?.restaurant_parent_id === storeId

    if (
      newOrderData &&
      (newOrderData.restaurant._id === storeId || isNewOrderFromChildRest) &&
      newOrderData.status === ORDER_STATUS_CODE.RESTAURANT_ACCEPTED
    ) {
      const isAutoAcceptEnabled = selectStoreAutoAcceptEnabled(
        listenerApi.getState()
      )

      if (!isAutoAcceptEnabled) {
        return
      }

      const hasInteracted = selectHasUserInteracted(listenerApi.getState())
      let storageAppId = localStorage.getItem(NOTIFICATION_APP_ID)
      const currentAppId = selectNotificationAppId(listenerApi.getState())
      if (!storageAppId && currentAppId) {
        localStorage.setItem(NOTIFICATION_APP_ID, String(currentAppId))
        storageAppId = currentAppId
      }
      const isNotificationTab = currentAppId === storageAppId

      listenerApi.dispatch(
        removeOrderIdFromPending({ orderId: newOrderData.order_id })
      )
      listenerApi.dispatch(stopAudioNotification())

      createAutoAcceptBrowserNotification(newOrderData.order_id)

      if (isNotificationTab) {
        history.push(
          generatePath(PATHS.ORDERS_BY_STATUS, { status: 'in-progress' })
        )

        if (hasInteracted) {
          const orderId = newOrderData.order_id
          if (!recentAutoAcceptChimeOrderIds.has(orderId)) {
            recentAutoAcceptChimeOrderIds.add(orderId)
            window.setTimeout(() => {
              recentAutoAcceptChimeOrderIds.delete(orderId)
            }, 15000)

            listenerApi.dispatch(
              playAutoAcceptChimeNotification({
                orderId
              })
            )
          }
        }

        try {
          const details = await listenerApi
            .dispatch(
              ordersApi.endpoints.fetchOrderDetails.initiate({
                storeId,
                orderId: newOrderData.order_id
              })
            )
            .unwrap()

          listenerApi.dispatch(
            addAutoAcceptOrderNotification({
              orderDetails: {
                ...details,
                earnings: newOrderData.restaurant_payout
              },
              type: 'autoAcceptOrder'
            })
          )
        } catch (error) {
          if (isErrorType(error)) {
            console.error(
              'Failed to fetch order details for auto-accepted order:',
              error.message
            )
          } else {
            console.error(
              'Failed to fetch order details for auto-accepted order:',
              error
            )
          }
        }
      }

      listenerApi.dispatch(
        ordersApi.endpoints.getOrdersCountByTypes.initiate({
          store_id: storeId
        })
      )
      listenerApi.dispatch(
        ordersApi.util.invalidateTags([{ type: 'Orders', id: 'LIST' }])
      )
    }
  }
})

startAppListening({
  actionCreator: eventActions.order_rejected,
  effect: async (action, listenerApi) => {
    const newOrderData = action.payload as WSRejectedOrderMessage
    const storeId = selectStoreId(listenerApi.getState()) as string
    const flags = selectFlags(listenerApi.getState())
    const isChainsEnabled =
      flags?.lmd_store_web_restaurant_chains_feature || false

    const isNewOrderFromChildRest =
      isChainsEnabled &&
      storeId &&
      newOrderData?.restaurant_parent_id === storeId

    if (
      newOrderData &&
      (newOrderData?.restaurant_id === storeId || isNewOrderFromChildRest)
    ) {
      listenerApi.dispatch(
        removeOrderIdFromPending({ orderId: newOrderData.order_id })
      )

      listenerApi.dispatch(
        ordersApi.endpoints.getOrdersCountByTypes.initiate({
          store_id: storeId
        })
      )
      listenerApi.dispatch(
        ordersApi.util.invalidateTags([{ type: 'Orders', id: 'LIST' }])
      )
    }
  }
})

export const {
  resetOrdersStateToInitial,

  setOrderSearchParams,
  setOrderFilters,
  removeOrderFilter,
  setRemainingETAForOrder,
  saveDriverName
} = ordersSlice.actions

export const ordersInitialState = initialState
export default ordersSlice.reducer
